import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const NewYorkRequirementsPage = () => {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-cyan-50 via-white to-blue-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                NYDFS Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                New York (NYDFS) Cybersecurity Requirements – <span className="text-cyan-600">23 NYCRR 500</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                A complete breakdown of the NYDFS cybersecurity rules and how small financial services firms can achieve full compliance.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding NYDFS 23 NYCRR 500</h2>
              
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-8 mb-8">
                <p className="text-gray-700 mb-4">
                  The <strong>New York Department of Financial Services (NYDFS) Cybersecurity Regulation (23 NYCRR 500)</strong> is one of the most comprehensive and stringent state-level cybersecurity frameworks in the United States. Originally effective March 1, 2017, with significant amendments in 2023, this regulation establishes minimum cybersecurity standards for all financial services institutions licensed or regulated by NYDFS.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-start gap-3">
                      <i className="ri-building-line text-2xl text-cyan-600 flex-shrink-0 mt-1"></i>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Who Must Comply?</h3>
                        <p className="text-sm text-gray-700">
                          Any entity operating under a license, registration, charter, certificate, permit, accreditation, or similar authorization under the Banking Law, Insurance Law, or Financial Services Law.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-start gap-3">
                      <i className="ri-shield-check-line text-2xl text-cyan-600 flex-shrink-0 mt-1"></i>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Core Objective</h3>
                        <p className="text-sm text-gray-700">
                          Protect consumer data and ensure the safety and soundness of New York's financial services industry through comprehensive cybersecurity programs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
                <div className="flex items-start gap-4">
                  <i className="ri-error-warning-line text-2xl text-yellow-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Why These Rules Matter</h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                        <span><strong>Regulatory Enforcement:</strong> NYDFS actively examines and enforces compliance, with significant penalties for violations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                        <span><strong>Consumer Protection:</strong> Safeguarding sensitive financial and personal information is paramount</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                        <span><strong>Business Continuity:</strong> Proper cybersecurity reduces the risk of costly breaches and operational disruptions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                        <span><strong>Competitive Advantage:</strong> Demonstrating strong cybersecurity builds trust with clients and partners</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-50 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-3">High-Level Compliance Expectations</h3>
                <p className="text-gray-700 mb-4">
                  Covered entities must establish and maintain a comprehensive cybersecurity program designed to protect the confidentiality, integrity, and availability of their information systems. This includes:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <i className="ri-file-shield-2-line text-2xl text-cyan-600 mb-2"></i>
                    <p className="text-sm font-semibold text-gray-900">Written Policies</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <i className="ri-user-settings-line text-2xl text-cyan-600 mb-2"></i>
                    <p className="text-sm font-semibold text-gray-900">Designated CISO</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <i className="ri-calendar-check-line text-2xl text-cyan-600 mb-2"></i>
                    <p className="text-sm font-semibold text-gray-900">Annual Certification</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Requirements Accordions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Detailed NYDFS Requirements</h2>
                <p className="text-xl text-gray-600">
                  Click each section to view specific regulatory obligations and compliance expectations
                </p>
              </div>

              <div className="space-y-4">
                {/* Cybersecurity Program */}
                <RequirementAccordion
                  id="cybersecurity-program"
                  title="Cybersecurity Program – Section 500.02"
                  icon="ri-shield-check-line"
                  isOpen={openSection === 'cybersecurity-program'}
                  onToggle={() => toggleSection('cybersecurity-program')}
                >
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      Section 500.02 requires each covered entity to establish and maintain a <strong>cybersecurity program</strong> designed to protect the confidentiality, integrity, and availability of the entity's information systems.
                    </p>
                    
                    <div className="bg-white rounded-lg p-5 border-l-4 border-cyan-600">
                      <h4 className="font-bold text-gray-900 mb-3">Core Requirements</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-cyan-600 flex-shrink-0 mt-1"></i>
                          <span>Program must be based on a <strong>risk assessment</strong> that identifies reasonably foreseeable internal and external cybersecurity risks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-cyan-600 flex-shrink-0 mt-1"></i>
                          <span>Must use <strong>defensive infrastructure</strong> and implement policies and procedures to protect information systems</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-cyan-600 flex-shrink-0 mt-1"></i>
                          <span>Detect, respond to, and recover from cybersecurity events</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-cyan-600 flex-shrink-0 mt-1"></i>
                          <span>Fulfill regulatory reporting obligations</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-cyan-600 flex-shrink-0 mt-1"></i>
                          <span>Program must be <strong>appropriate to the size and complexity</strong> of the covered entity</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-5 border-l-4 border-cyan-600">
                      <h4 className="font-bold text-gray-900 mb-3">Board of Directors / Senior Officer Responsibilities</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-cyan-600 flex-shrink-0 mt-0.5"></i>
                          <span>The cybersecurity program must be approved by the board of directors or a senior officer</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-cyan-600 flex-shrink-0 mt-0.5"></i>
                          <span>Board or senior management must receive regular reports on the cybersecurity program</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-cyan-600 flex-shrink-0 mt-0.5"></i>
                          <span>2023 amendments expanded board oversight requirements, particularly for Class A companies</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </RequirementAccordion>

                {/* Add more accordion sections here - I'll create a summary component */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <p className="text-gray-700 text-center">
                    <strong>Additional sections include:</strong> Cybersecurity Policy (500.03), CISO Requirements (500.04), Penetration Testing (500.05), Audit Trail (500.06), Access Controls (500.07), Application Security (500.08), Risk Assessment (500.09), Personnel Training (500.10), Third-Party Security (500.11), Multi-Factor Authentication (500.12), Data Retention (500.13), Monitoring & Encryption (500.14-500.16), 72-Hour Reporting (500.17), Exemptions (500.19), Annual Certification, Recordkeeping, and 2023 Amendments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who Must Comply Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Who Must Comply?</h2>
                <p className="text-xl text-gray-600">
                  NYDFS 23 NYCRR 500 applies to all entities licensed or regulated by the New York Department of Financial Services
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Mortgage banks and mortgage brokers',
                  'Insurance companies and producers',
                  'Licensed lenders and consumer finance companies',
                  'Money transmitters and virtual currency businesses',
                  'Investment advisers and broker-dealers',
                  'Premium finance agencies',
                  'Sales finance companies',
                  'Budget planners and debt adjusters',
                  'Any other entity licensed or regulated by NYDFS'
                ].map((entity, index) => (
                  <div key={index} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-5 border border-cyan-100">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <p className="text-gray-900 font-medium">{entity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-cyan-50 border-l-4 border-cyan-600 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>Note:</strong> If your organization is licensed or regulated by NYDFS in any capacity, you are subject to this regulation regardless of your size or the volume of business you conduct in New York.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How MortgageMoat Helps Section */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">How MortgageMoat Helps You Comply</h2>
                <p className="text-xl text-gray-300">
                  We specialize in helping small financial services firms achieve and maintain NYDFS compliance
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {[
                  {
                    icon: 'ri-file-list-3-line',
                    title: 'Comprehensive Risk Assessments',
                    description: 'We conduct thorough risk assessments tailored to your business, identifying vulnerabilities and prioritizing remediation efforts to meet Section 500.09 requirements.'
                  },
                  {
                    icon: 'ri-file-shield-2-line',
                    title: 'NYDFS-Compliant Policies & Procedures',
                    description: 'Receive customized, written cybersecurity policies that address all requirements of Section 500.03, ready for board approval and implementation.'
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Managed Cybersecurity Services',
                    description: 'Our team serves as your virtual CISO, providing ongoing monitoring, incident response, vulnerability management, and compliance oversight.'
                  },
                  {
                    icon: 'ri-team-line',
                    title: 'Employee Awareness Training',
                    description: 'Engaging, NYDFS-focused training programs that satisfy Section 500.10 requirements and reduce your human risk factor.'
                  },
                  {
                    icon: 'ri-links-line',
                    title: 'Vendor Risk Management',
                    description: 'We help you assess and manage third-party cybersecurity risks per Section 500.11, including due diligence and ongoing monitoring.'
                  },
                  {
                    icon: 'ri-file-text-line',
                    title: 'Annual Certification Support',
                    description: 'We prepare all documentation and supporting evidence needed for your annual Section 500.17(b) certification, ensuring accuracy and completeness.'
                  },
                  {
                    icon: 'ri-bug-line',
                    title: 'Penetration Testing & Vulnerability Assessments',
                    description: 'Annual penetration testing and continuous vulnerability scanning to meet Section 500.05 requirements and strengthen your defenses.'
                  },
                  {
                    icon: 'ri-alarm-warning-line',
                    title: 'Incident Response Planning & Support',
                    description: 'Develop and test incident response plans that meet Section 500.16 requirements, with 24/7 support when incidents occur.'
                  }
                ].map((service, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                    <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-4">
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                    <p className="text-gray-300 text-sm">{service.description}</p>
                  </div>
                ))}
              </div>

              <div className="bg-cyan-600 rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Achieve NYDFS Compliance?</h3>
                <p className="text-lg mb-6 text-cyan-50">
                  Let MortgageMoat guide you through every requirement of 23 NYCRR 500 with expert support tailored to small financial firms.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/schedule-call"
                    className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-line"></i>
                    Schedule a Consultation
                  </a>
                  <a
                    href="/risk-assessment"
                    className="inline-flex items-center justify-center gap-2 bg-cyan-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-cyan-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shield-check-line"></i>
                    Get a Risk Assessment
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Reusable Accordion Component
interface RequirementAccordionProps {
  id: string;
  title: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const RequirementAccordion: React.FC<RequirementAccordionProps> = ({
  title,
  icon,
  isOpen,
  onToggle,
  children
}) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
            <i className={`${icon} text-2xl text-cyan-600`}></i>
          </div>
          <h3 className="text-lg font-bold text-gray-900 text-left">{title}</h3>
        </div>
        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-2xl text-gray-400 flex-shrink-0`}></i>
      </button>
      
      {isOpen && (
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

export default NewYorkRequirementsPage;
